// __private-exports
/*
 * Copyright 2026 Hypergiant Galactic Systems Inc. All rights reserved.
 * This file is licensed to you under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License. You may obtain a copy
 * of the License at https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software distributed under
 * the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR REPRESENTATIONS
 * OF ANY KIND, either express or implied. See the License for the specific language
 * governing permissions and limitations under the License.
 */

import type {
  RuleType,
  RuleValidator,
  ValidationResult,
} from 'react-querybuilder';

/**
 * Runs the validator on a rule and normalizes the result.
 * Returns { valid: true } if no validator is provided.
 *
 * @example
 * ```tsx
 * const rule = { field: 'age', operator: 'greaterThan', value: 18 };
 * const validator = (r) => r.value > 0;
 * const result = getValidationResult(rule, validator); // { valid: true }
 * ```
 *
 * @param rule - The rule to validate.
 * @param validator - Optional validator function for the rule.
 * @returns Normalized validation result with valid flag and optional reasons.
 */
export const getValidationResult = (
  rule: RuleType,
  validator?: RuleValidator,
): ValidationResult => {
  if (!validator) {
    return { valid: true };
  }

  const result = validator(rule);

  if (typeof result === 'boolean') {
    return { valid: result };
  }

  return {
    valid: result.valid,
    reasons: result.valid ? [] : result.reasons,
  };
};
